/**
 * RouteGuard Component
 * 
 * A unified route protection component that handles all authentication
 * and authorization scenarios in one place.
 * 
 * Replaces: ProtectedRoute, AdminRoute, ZeroAccessRoute, ModuleRoute, PublicRoute
 * 
 * @example
 * // Public route (login page)
 * <RouteGuard type="public"><Login /></RouteGuard>
 * 
 * // Protected route (requires USER role)
 * <RouteGuard type="protected"><Home /></RouteGuard>
 * 
 * // Admin route
 * <RouteGuard type="admin"><UserManagement /></RouteGuard>
 * 
 * // Module route with permission check
 * <RouteGuard type="module" moduleId="finance" requiredLevel="view">
 *   <FinancePage />
 * </RouteGuard>
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, type ModuleAccessLevel } from '../context/AuthContext';

// =============================================================================
// Types
// =============================================================================

type RouteType = 'public' | 'protected' | 'admin' | 'zero-access' | 'module';

interface RouteGuardProps {
  children: React.ReactNode;
  type: RouteType;
  moduleId?: string;
  requiredLevel?: ModuleAccessLevel;
}

// =============================================================================
// Loading Component
// =============================================================================

const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="relative">
        {/* Outer ring */}
        <div className="w-16 h-16 border-4 border-primary-100 rounded-full" />
        {/* Spinning ring */}
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-primary-600 rounded-full animate-spin" />
      </div>
      <p className="mt-4 text-gray-600 font-medium">טוען...</p>
    </div>
  </div>
);

// =============================================================================
// Component
// =============================================================================

const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  type,
  moduleId,
  requiredLevel = 'view',
}) => {
  const { isAuthenticated, isLoading, hasAccessRole, isAdmin, hasModuleAccess } = useAuth();

  // Show loading while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Handle different route types
  switch (type) {
    // =========================================================================
    // Public Route - For unauthenticated users (login page)
    // =========================================================================
    case 'public':
      if (isAuthenticated) {
        return hasAccessRole('USER')
          ? <Navigate to="/" replace />
          : <Navigate to="/zero-access" replace />;
      }
      return <>{children}</>;

    // =========================================================================
    // Zero Access Route - For authenticated users awaiting approval
    // =========================================================================
    case 'zero-access':
      if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
      }
      // If user has access, redirect to home
      if (hasAccessRole('USER')) {
        return <Navigate to="/" replace />;
      }
      return <>{children}</>;

    // =========================================================================
    // Protected Route - Requires USER role minimum
    // =========================================================================
    case 'protected':
      if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
      }
      if (!hasAccessRole('USER')) {
        return <Navigate to="/zero-access" replace />;
      }
      return <>{children}</>;

    // =========================================================================
    // Admin Route - Requires ADMIN role
    // =========================================================================
    case 'admin':
      if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
      }
      if (!hasAccessRole('USER')) {
        return <Navigate to="/zero-access" replace />;
      }
      if (!isAdmin) {
        return <Navigate to="/" replace />;
      }
      return <>{children}</>;

    // =========================================================================
    // Module Route - Requires module-specific permission
    // =========================================================================
    case 'module':
      if (!moduleId) {
        console.error('RouteGuard: moduleId is required for module type routes');
        return <Navigate to="/" replace />;
      }
      if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
      }
      if (!hasAccessRole('USER')) {
        return <Navigate to="/zero-access" replace />;
      }
      if (!hasModuleAccess(moduleId, requiredLevel)) {
        return <Navigate to="/" replace />;
      }
      return <>{children}</>;

    default:
      return <>{children}</>;
  }
};

export default RouteGuard;

// =============================================================================
// Convenience Wrapper Components (for backward compatibility)
// =============================================================================

export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteGuard type="public">{children}</RouteGuard>
);

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteGuard type="protected">{children}</RouteGuard>
);

export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteGuard type="admin">{children}</RouteGuard>
);

export const ZeroAccessRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RouteGuard type="zero-access">{children}</RouteGuard>
);

interface ModuleRouteProps {
  children: React.ReactNode;
  moduleId: string;
  requiredLevel?: ModuleAccessLevel;
}

export const ModuleRoute: React.FC<ModuleRouteProps> = ({ children, moduleId, requiredLevel }) => (
  <RouteGuard type="module" moduleId={moduleId} requiredLevel={requiredLevel}>
    {children}
  </RouteGuard>
);
